/**
 * Environment Configuration for Consumer Portal
 *
 * Centralized environment variable management with Zod validation.
 * All environment variables should be accessed through this module.
 *
 * @module config/env
 */

import { z } from 'zod';
import { URLS } from './urls';

// Helper to convert empty strings to undefined for Zod defaults
const emptyToUndefined = (val: string | undefined) => (val === '' ? undefined : val);

/**
 * Environment schema with validation rules
 * Portal URLs read from ports.json (single source of truth) - can be overridden by env vars
 * For local dev: uses ports.json defaults
 * For staging/production: override with env vars from domains.json
 */
const envSchema = z.object({
  // API URLs
  // Local dev: reads from ports.json (URLS constant)
  // Production: requires env var (no fallback) to catch pipeline issues
  VITE_API_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.API_GATEWAY)
    : z.preprocess(emptyToUndefined, z.string().url('API URL is required in production')),
  VITE_AUTH_PORTAL_URL: import.meta.env.DEV
    ? z.preprocess(emptyToUndefined, z.string().url().optional()).transform((v) => v ?? URLS.AUTH_PORTAL)
    : z.preprocess(emptyToUndefined, z.string().url('Auth portal URL is required in production')),

  // Firebase - Optional for local development
  // Use preprocess to handle empty strings from Docker builds
  VITE_FIREBASE_API_KEY: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_AUTH_DOMAIN: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_PROJECT_ID: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_STORAGE_BUCKET: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.preprocess(emptyToUndefined, z.string().optional()),
  VITE_FIREBASE_APP_ID: z.preprocess(emptyToUndefined, z.string().optional()),

  // Firebase Cloud Messaging (Push Notifications)
  VITE_FIREBASE_VAPID_KEY: z.preprocess(emptyToUndefined, z.string().optional()),

  // Entity Configuration
  VITE_DEALS_ENTITY_ID: z.string().default('deals'),

  // OpenTelemetry Browser Tracing (Issue #469)
  // Return undefined if not set (or empty string), so ?? operator works for default
  VITE_OTEL_ENABLED: z
    .string()
    .optional()
    .transform((val) => (val === undefined || val === '' ? undefined : val === 'true')),
  VITE_OTEL_COLLECTOR_URL: z.string().url().optional(),
  VITE_OTEL_SAMPLE_RATE: z.coerce.number().min(0).max(1).optional(),

  // Feature flags (Issue #1308 - Term Sheet E-Signature MVP)
  // Gates the "Documents to sign" route + nav entry + homepage widget.
  // When false, the nav entry is hidden and the route returns 404.
  //
  // In local dev the flag defaults to TRUE — provisioning writes it into
  // .env.local on every dev:all startup (see scripts/lib/provision.sh,
  // Feature Flags section), but if a developer runs Vite against a stale
  // .env.local the dev fallback keeps the feature exercisable. Production
  // still fails closed: only an explicit 'true' / '1' enables it, matching
  // the GrowthBook flag rollout surface. Per CLAUDE.md §7a (dev-only
  // fallbacks OK; production fails fast).
  VITE_ENABLE_TERMSHEET_SIGNING_V2: import.meta.env.DEV
    ? z
        .string()
        .optional()
        // Dev: default TRUE; only explicit 'false' / '0' disables it.
        .transform((val) => {
          const normalized = (val ?? '').trim().toLowerCase();
          if (normalized === 'false' || normalized === '0') return false;
          return true;
        })
    : z
        .string()
        .optional()
        // Prod: default FALSE; only explicit 'true' / '1' enables it.
        .transform((val) => (val === undefined || val === '' ? false : val === 'true' || val === '1')),
});

/**
 * Validate environment variables at startup
 */
function validateEnv() {
  const parsed = envSchema.safeParse(import.meta.env);

  if (!parsed.success) {
    const errors = parsed.error.flatten().fieldErrors;
    console.error('[Consumer Portal] Invalid environment variables:', errors);

    // In development, throw to fail fast
    if (import.meta.env.DEV) {
      throw new Error(`Invalid environment variables: ${JSON.stringify(errors)}`);
    }
  }

  return parsed.success ? parsed.data : envSchema.parse({});
}

/**
 * Validated environment configuration
 * Use this throughout the application instead of direct import.meta.env access
 */
export const env = validateEnv();

/**
 * Type-safe environment variable access
 */
export type Env = z.infer<typeof envSchema>;

export default env;
